import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import {
    Settings, Download, ChevronLeft, ChevronRight,
    Search, PenTool, Move, Type, Award, Fingerprint, X
} from "lucide-react";
import API from '../../../services/API';

const UniversalCertificateGenerator = () => {
    // Selection States
    const [category, setCategory] = useState("student");
    const [selectedStudents, setSelectedStudents] = useState([]); // Array for multi-select
    const [selectedClass, setSelectedClass] = useState("");

    // Search & Loading States
    const [studentSearch, setStudentSearch] = useState("");
    const [studentResults, setStudentResults] = useState([]);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [classes, setClasses] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);

    // Preview & Action States
    const [preview, setPreview] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    // Certificate Content & Styling
    const [certData, setCertData] = useState({
        title: "SIJIL PENGHARGAAN",
        recipient: "NAMA MURID",
        ic_number: "",
        recipient_font_size: 95,
        reason: "Atas sumbangan dan pencapaian cemerlang dalam",
        event: "PROGRAM SAHSIAH UNGGUL MURID (SUMUR)",
        date: new Date().toLocaleDateString("ms-MY", { day: "numeric", month: "long", year: "numeric" }),
        signatory: "PENGETUA CEMERLANG",
        signature_x: 0.50,
        signature_y: 0.85,
        signature_width: 0.20
    });

    const templates = Array.from({ length: 13 }, (_, i) => `cert_${i + 1}`);
    const [templateIndex, setTemplateIndex] = useState(0);
    const template = templates[templateIndex];

    const nextTemplate = () => setTemplateIndex((prev) => (prev + 1) % templates.length);
    const prevTemplate = () => setTemplateIndex((prev) => prev === 0 ? templates.length - 1 : prev - 1);

    // Side Effects
    useEffect(() => {
        const delay = setTimeout(() => {
            if (category === "student" && studentSearch.length >= 2) searchStudents(studentSearch);
            else setStudentResults([]);
        }, 300);
        return () => clearTimeout(delay);
    }, [studentSearch, category]);

    useEffect(() => { if (category === "class") loadClasses(); }, [category]);

    useEffect(() => {
        const delay = setTimeout(() => previewCertificate(), 400);
        return () => clearTimeout(delay);
    }, [certData, template]);

    // Data Fetching
    const loadClasses = async () => {
        try {
            setLoadingClasses(true);
            const response = await API.get('/portal/getClasses', { headers: { token: localStorage.getItem("token") } });
            if (response.data.response_code === 1000) setClasses(response.data.data);
        } catch (err) { toast.error("Gagal memuatkan senarai kelas"); } finally { setLoadingClasses(false); }
    };

    const searchStudents = async (q) => {
        try {
            setLoadingStudents(true);
            const res = await API.get("/portal/getStudents", {
                headers: { token: localStorage.getItem("token") },
                params: { page: 1, limit: 10, search: q }
            });
            if (res.data.response_code === 1000) setStudentResults(res.data.data?.students || []);
        } catch (err) { console.error(err); } finally { setLoadingStudents(false); }
    };

    // Actions
    const toggleStudentSelection = (student) => {
        const studentInfo = {
            uuid: student.student_uuid,
            name: student.moeis_name || student.first_name,
            ic: student.ic_number || ""
        };

        setSelectedStudents(prev => {
            const exists = prev.find(s => s.uuid === studentInfo.uuid);
            if (exists) return prev.filter(s => s.uuid !== studentInfo.uuid);
            return [...prev, studentInfo];
        });

        // Update mockup to the latest selection
        setCertData(prev => ({
            ...prev,
            recipient: studentInfo.name,
            ic_number: studentInfo.ic
        }));

        setStudentSearch("");
        setStudentResults([]);
    };

    const previewCertificate = async () => {
        try {
            const res = await API.post("/eCertificate/preview", { ...certData, template }, {
                headers: { token: localStorage.getItem("token") },
                responseType: "blob"
            });
            const url = URL.createObjectURL(res.data);
            setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
        } catch (err) { console.error("Preview fail", err); }
    };

    const downloadSijil = async () => {
        if (category === "class" && !selectedClass) return toast.warning("Sila pilih kelas.");
        if (category === "student" && selectedStudents.length === 0) return toast.warning("Sila pilih sekurang-kurangnya satu murid.");

        setIsGenerating(true);
        try {
            const isBatch = category === "class" || selectedStudents.length > 1;
            const endpoint = isBatch ? "/eCertificate/certificate/generate/class" : "/eCertificate/generate";

            const res = await API.post(endpoint, {
                ...certData,
                template,
                category,
                student_uuid: selectedStudents[0]?.uuid,
                student_uuids: selectedStudents.map(s => s.uuid),
                class_id: selectedClass
            }, {
                headers: { token: localStorage.getItem("token") },
                responseType: "blob"
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", isBatch ? `Batch_Sijil_${new Date().getTime()}.zip` : `Sijil_${certData.recipient}.png`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Muat turun berjaya!");
        } catch (err) { toast.error("Gagal menjana sijil."); } finally { setIsGenerating(false); }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCertData(prev => ({ ...prev, [name]: value }));
    };

    const Label = ({ children }) => (
        <label className="form-label small fw-bold mb-1 text-secondary text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>{children}</label>
    );

    return (
        <div className="container-fluid py-4 min-vh-100" style={{ backgroundColor: "#f4f7f6" }}>
            {/* Header */}
            <div className="row mb-4 align-items-center bg-white p-3 mx-1 rounded-4 shadow-sm border">
                <div className="col">
                    <div className="d-flex align-items-center gap-3">
                        <div className="p-2 rounded-3 bg-primary bg-opacity-10 text-white border border-primary border-opacity-25">
                            <Award size={28} />
                        </div>
                        <div>
                            <h3 className="fw-black mb-0 text-black" style={{ letterSpacing: '-1.5px' }}>Studio Sijil</h3>
                            <p className="text-muted small mb-0 fw-medium">Penjana e-Sijil Automatik Sekolah</p>
                        </div>
                    </div>
                </div>
                <div className="col-auto">
                    <button className={`btn btn-primary px-4 py-2 fw-bold shadow-sm d-flex align-items-center gap-2 ${isGenerating ? 'disabled' : ''}`} onClick={downloadSijil}>
                        {isGenerating ? <span className="spinner-border spinner-border-sm" /> : <Download size={18} />}
                        {isGenerating ? 'Menjana...' : 'Muat Turun Sijil'}
                    </button>
                </div>
            </div>

            <div className="row g-4">
                {/* Left Panel: Configuration */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden" style={{ height: '82vh' }}>
                        <div className="card-header bg-white border-bottom p-4">
                            <div className="d-flex align-items-center gap-2">
                                <Settings size={18} className="text-primary" />
                                <h6 className="mb-0 fw-black text-dark text-uppercase">Konfigurasi</h6>
                            </div>
                        </div>
                        <div className="card-body p-4 overflow-auto">

                            <div className="mb-4">
                                <Label>Kategori Penerima</Label>
                                <div className="btn-group w-100 border rounded-3 p-1 bg-light shadow-sm" style={{ backgroundColor: "#f8f9fa" }}>
                                    <button
                                        className={`btn btn-sm py-2 rounded-2 fw-bold transition-all ${category === 'student'
                                                ? 'btn-primary shadow-sm text-white'
                                                : 'btn-light border-0 text-dark opacity-75'
                                            }`}
                                        onClick={() => setCategory('student')}
                                        style={{ flex: 1 }}
                                    >
                                        Individu
                                    </button>
                                    <button
                                        className={`btn btn-sm py-2 rounded-2 fw-bold transition-all ${category === 'class'
                                                ? 'btn-primary shadow-sm text-white'
                                                : 'btn-light border-0 text-dark opacity-75'
                                            }`}
                                        onClick={() => setCategory('class')}
                                        style={{ flex: 1 }}
                                    >
                                        Kelas
                                    </button>
                                </div>
                            </div>

                            {category === "student" && (
                                <div className="mb-4">
                                    <Label>Senarai Murid Terpilih ({selectedStudents.length})</Label>
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        {selectedStudents.map(s => (
                                            <div key={s.uuid} className="badge bg-primary bg-opacity-10 text-white border border-primary border-opacity-25 d-flex align-items-center gap-2 p-2 rounded-pill fw-bold">
                                                {s.name}
                                                <X size={14} className="cursor-pointer" onClick={() => setSelectedStudents(p => p.filter(x => x.uuid !== s.uuid))} />
                                            </div>
                                        ))}
                                        {selectedStudents.length === 0 && <div className="text-muted small italic p-2 border border-dashed rounded w-100 text-center">Sila cari & pilih murid</div>}
                                    </div>

                                    <div className="position-relative">
                                        <div className="input-group shadow-sm border rounded-3 overflow-hidden">
                                            <span className="input-group-text bg-white border-0"><Search size={16} className="text-muted" /></span>
                                            <input className="form-control border-0" placeholder="Taip nama murid..." value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} />
                                        </div>

                                        {studentResults.length > 0 && (
                                            <div className="list-group shadow-lg border rounded-3 position-absolute w-100 mt-1 z-3 overflow-auto" style={{ maxHeight: '200px' }}>
                                                {studentResults.map(s => (
                                                    <button key={s.student_uuid} className="list-group-item list-group-item-action py-3 border-0 border-bottom d-flex justify-content-between align-items-center" onClick={() => toggleStudentSelection(s)}>
                                                        <div><div className="fw-bold text-black">{s.first_name}</div><div className="small text-muted">{s.class_name}</div></div>
                                                        <PenTool size={14} className="text-primary opacity-50" />
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {category === "class" && (
                                <div className="mb-4">
                                    <Label>Pilih Kelas</Label>
                                    <select className="form-select border shadow-sm fw-bold" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                                        <option value="">-- Pilih Kelas --</option>
                                        {classes.map((c) => (<option key={c.class_id} value={c.class_id}>{c.class_name}</option>))}
                                    </select>
                                </div>
                            )}

                            <hr className="my-4 opacity-10" />

                            <div className="mb-3">
                                <Label>Tajuk Sijil</Label>
                                <input name="title" className="form-control border shadow-sm fw-medium" value={certData.title} onChange={handleInputChange} />
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-8">
                                    <Label>Nama Penerima</Label>
                                    <input name="recipient" className="form-control border shadow-sm fw-medium" value={certData.recipient} onChange={handleInputChange} />
                                </div>
                                <div className="col-4">
                                    <Label>Saiz Font</Label>
                                    <div className="input-group shadow-sm border rounded-3 overflow-hidden">
                                        <input type="number" name="recipient_font_size" className="form-control border-0 fw-black text-center" value={certData.recipient_font_size} onChange={handleInputChange} />
                                        <span className="input-group-text bg-light border-0 px-2 fw-bold text-muted" style={{ fontSize: '10px' }}>PX</span>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <Label>No. KP / Sijil Lahir</Label>
                                <div className="input-group shadow-sm border rounded-3 overflow-hidden">
                                    <span className="input-group-text bg-white border-0"><Fingerprint size={16} className="text-muted" /></span>
                                    <input name="ic_number" className="form-control border-0 fw-medium" placeholder="Auto-fill selepas pilih murid" value={certData.ic_number} onChange={handleInputChange} />
                                </div>
                            </div>

                            <div className="mb-3">
                                <Label>Nama Program / Acara</Label>
                                <input name="event" className="form-control border shadow-sm fw-medium" value={certData.event} onChange={handleInputChange} />
                            </div>

                            <div className="mb-3">
                                <Label>Tarikh Sijil</Label>
                                <input name="date" className="form-control border shadow-sm fw-medium" value={certData.date} onChange={handleInputChange} />
                            </div>

                            <div className="mb-3">
                                <Label>Penganugerahan / Jawatan</Label>
                                <input name="signatory" className="form-control border shadow-sm fw-medium" value={certData.signatory} onChange={handleInputChange} />
                            </div>

                            <div className="mb-4">
                                <Label>Penerangan Ringkas</Label>
                                <textarea name="reason" className="form-control border shadow-sm fw-medium" rows="3" value={certData.reason} onChange={handleInputChange} />
                            </div>

                            {/* Signature Adjustment */}
                            <div className="p-3 bg-light rounded-4 border">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <Move size={16} className="text-primary" />
                                    <h6 className="mb-0 fw-black text-dark small text-uppercase">Pelarasan Tandatangan</h6>
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <Label>Posisi X ({Math.round(certData.signature_x * 100)}%)</Label>
                                        <input type="range" className="form-range custom-range" min="0.1" max="0.9" step="0.01" value={certData.signature_x} onChange={(e) => setCertData(p => ({ ...p, signature_x: parseFloat(e.target.value) }))} />
                                    </div>
                                    <div className="col-6">
                                        <Label>Posisi Y ({Math.round(certData.signature_y * 100)}%)</Label>
                                        <input type="range" className="form-range custom-range" min="0.5" max="0.95" step="0.01" value={certData.signature_y} onChange={(e) => setCertData(p => ({ ...p, signature_y: parseFloat(e.target.value) }))} />
                                    </div>
                                    <div className="col-12">
                                        <Label>Lebar ({Math.round(certData.signature_width * 100)}%)</Label>
                                        <input type="range" className="form-range custom-range" min="0.1" max="0.5" step="0.01" value={certData.signature_width} onChange={(e) => setCertData(p => ({ ...p, signature_width: parseFloat(e.target.value) }))} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Panel: Mockup */}
                <div className="col-lg-8">
                    <div className="card border-0 shadow-lg rounded-4 bg-dark overflow-hidden sticky-top" style={{ top: '20px' }}>
                        <div className="card-header bg-dark bg-opacity-50 border-bottom border-white border-opacity-10 d-flex justify-content-between align-items-center p-3">
                            <div className="d-flex align-items-center gap-2">
                                <span className="p-1 bg-success rounded-circle animate-pulse" style={{ width: '8px', height: '8px' }}></span>
                                <span className="text-white-50 fw-bold text-uppercase" style={{ fontSize: '11px', letterSpacing: '1px' }}>Mockup Studio Live</span>
                            </div>
                            <div className="d-flex align-items-center bg-white bg-opacity-10 rounded-pill p-1 border border-white border-opacity-10 shadow-sm">
                                <button
                                    className="btn btn-link text-white p-2 rounded-circle hover-bg-white hover-bg-opacity-10 border-0 transition-all"
                                    onClick={prevTemplate}
                                    style={{ lineHeight: 0 }}
                                >
                                    <ChevronLeft size={18} className="opacity-75" />
                                </button>

                                <div className="px-3 border-start border-end border-white border-opacity-10">
                                    <span className="text-white fw-black small text-uppercase tracking-widest" style={{ fontSize: '10px', minWidth: '90px', display: 'inline-block', textAlign: 'center' }}>
                                        {template.replace('_', ' ')}
                                    </span>
                                </div>

                                <button
                                    className="btn btn-link text-white p-2 rounded-circle hover-bg-white hover-bg-opacity-10 border-0 transition-all"
                                    onClick={nextTemplate}
                                    style={{ lineHeight: 0 }}
                                >
                                    <ChevronRight size={18} className="opacity-75" />
                                </button>
                            </div>
                        </div>
                        <div className="card-body d-flex justify-content-center align-items-center p-5" style={{ height: '70vh', background: 'radial-gradient(circle, #2c3136 0%, #1a1d20 100%)' }}>
                            {preview ? (
                                <div className="mockup-container p-2 bg-white shadow-2xl rounded-1 transform transition-all duration-500 hover:scale-105" style={{ maxWidth: '90%' }}>
                                    <img src={preview} alt="Certificate Preview" style={{ maxHeight: '55vh', width: 'auto', display: 'block' }} className="shadow-lg border rounded-1" />
                                </div>
                            ) : (
                                <div className="text-center text-secondary">
                                    <div className="spinner-border text-primary mb-3" />
                                    <p className="small text-uppercase tracking-widest fw-bold opacity-50">Menyusun Layout Sijil...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UniversalCertificateGenerator;